import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, ChevronRight } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useNavigate } from 'react-router-dom';

export default function LaborCostWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiRequest('/store-ops/labor-cost/widget');
        setData(result);
      } catch (error) {
        console.error('Failed to load labor cost data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="labor-cost-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Labor Cost %
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-8 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const storeSummary = data?.store_summary || [];
  const overTarget = data?.stores_over_target || 0;
  const target = data?.target || '25%';

  return (
    <Card data-testid="labor-cost-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            Labor Cost Tracker
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/labor-cost')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Target Info */}
        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <span className="text-sm text-slate-600">Target: {target} of sales</span>
          {overTarget > 0 ? (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {overTarget} over target
            </Badge>
          ) : (
            <Badge className="bg-green-100 text-green-700">All on target</Badge>
          )}
        </div>

        {/* Store Summary */}
        {storeSummary.length > 0 ? (
          <div className="space-y-3">
            <div className="text-xs font-medium text-slate-500 uppercase">{data?.period}</div>
            {storeSummary.map((store, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{store.store}</span>
                  <span className={`font-bold flex items-center gap-1 ${
                    store.status === 'good' ? 'text-green-600' : 
                    store.status === 'warning' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {store.avg_labor_percentage}%
                    {store.avg_labor_percentage <= 25 ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : (
                      <TrendingUp className="h-3 w-3" />
                    )}
                  </span>
                </div>
                <Progress 
                  value={Math.min(100, (store.avg_labor_percentage / 40) * 100)} 
                  className={`h-2 ${
                    store.status === 'good' ? '[&>div]:bg-green-500' : 
                    store.status === 'warning' ? '[&>div]:bg-yellow-500' : '[&>div]:bg-red-500'
                  }`}
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>${store.total_labor_cost?.toLocaleString()} labor</span>
                  <span>${store.total_sales?.toLocaleString()} sales</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">
            <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No sales data available</p>
            <p className="text-xs mt-1">Enter daily sales to track labor costs</p>
          </div>
        )}

        {/* Warning for over-target stores */}
        {(data?.over_target_stores || []).length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Stores Over Target
            </div>
            {data.over_target_stores.map((store, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-1 text-red-600">
                <span>{store.store}</span>
                <span className="font-bold">{store.avg_labor_percentage}%</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
